import type { PreparedFeature } from './feature-preparation.js';
import type {
  PreparationMessage,
  PreparationRequest,
  PreparationResponse,
  PreparationResult,
  PreparationService,
} from './preparation-protocol.js';
import type { PreparedTerrain } from './terrain-preparation.js';
import type { DigitalTwinFeature } from '../../types/digital-twin.js';
import type { GeoBounds } from '../../types/terrain-types.js';

export interface PreparationWorker {
  onmessage: ((event: MessageEvent<PreparationResponse>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  onmessageerror: ((event: MessageEvent) => void) | null;
  postMessage(message: PreparationMessage, transfer: Transferable[]): void;
  terminate(): void;
}

interface Job {
  id: number;
  request: PreparationRequest;
  resolve: (result: PreparationResult) => void;
  reject: (error: unknown) => void;
  cleanup: () => void;
}

interface Slot {
  worker: PreparationWorker;
  job?: Job;
}

/** Unlike the point-cloud pool, this owner bounds both busy and idle workers. */
export class PreparationWorkerClient implements PreparationService {
  private readonly queue = new Set<Job>();
  private readonly slots = new Set<Slot>();
  private nextId = 0;
  private disposed = false;

  constructor(
    private readonly factory: () => PreparationWorker = () =>
      new Worker(new URL('../../workers/terrain/PreparationWorker.ts', import.meta.url), {
        type: 'module',
      }),
    private readonly concurrency = Math.max(
      1,
      Math.min(2, (globalThis.navigator?.hardwareConcurrency ?? 4) - 1),
    ),
  ) {
    if (!Number.isInteger(concurrency) || concurrency < 1)
      throw new Error('Invalid preparation worker count');
  }

  async features(
    features: DigitalTwinFeature[],
    origin: [number, number, number],
    signal?: AbortSignal,
  ): Promise<PreparedFeature[]> {
    const result = await this.run({ kind: 'features', features, origin }, signal);
    if (result.kind !== 'features') throw new Error('Unexpected feature preparation response');
    return result.features;
  }

  async vector(
    request: Extract<PreparationRequest, { kind: 'vector' }>,
    signal?: AbortSignal,
  ): Promise<PreparedFeature[]> {
    const result = await this.run(request, signal);
    if (result.kind !== 'features') throw new Error('Unexpected vector preparation response');
    return result.features;
  }

  async terrain(
    request: Extract<PreparationRequest, { kind: 'terrain' }>,
    signal?: AbortSignal,
  ): Promise<PreparedTerrain> {
    const result = await this.run(request, signal);
    if (result.kind !== 'terrain') throw new Error('Unexpected terrain preparation response');
    return result.terrain;
  }

  async uvs(
    positions: Float32Array,
    bounds: GeoBounds,
    signal?: AbortSignal,
  ): Promise<Float32Array> {
    const result = await this.run({ kind: 'uv', positions, bounds }, signal);
    if (result.kind !== 'uv') throw new Error('Unexpected UV preparation response');
    return result.uvs;
  }

  dispose(): void {
    this.disposed = true;
    for (const job of this.queue) this.reject(job, new DOMException('Aborted', 'AbortError'));
    for (const slot of this.slots) {
      slot.worker.terminate();
      if (slot.job) this.reject(slot.job, new DOMException('Aborted', 'AbortError'));
      slot.job = undefined;
    }
    this.slots.clear();
  }

  private run(request: PreparationRequest, signal?: AbortSignal): Promise<PreparationResult> {
    return new Promise((resolve, reject) => {
      if (this.disposed || signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const abort = (): void => {
        this.reject(job, new DOMException('Aborted', 'AbortError'));
        for (const slot of this.slots) {
          if (slot.job !== job) continue;
          slot.job = undefined;
          slot.worker.terminate();
          this.slots.delete(slot);
        }
        this.pump();
      };
      const job: Job = {
        id: ++this.nextId,
        request,
        resolve,
        reject,
        cleanup: () => signal?.removeEventListener('abort', abort),
      };
      signal?.addEventListener('abort', abort, { once: true });
      this.queue.add(job);
      this.pump();
    });
  }

  private reject(job: Job, error: unknown): void {
    this.queue.delete(job);
    job.cleanup();
    job.reject(error);
  }

  private pump(): void {
    if (this.disposed) return;
    while (this.queue.size) {
      let slot = [...this.slots].find(candidate => !candidate.job);
      if (!slot && this.slots.size >= this.concurrency) return;
      const job = this.queue.values().next().value;
      if (!job) return;
      this.queue.delete(job);
      try {
        if (!slot) {
          slot = { worker: this.factory() };
          this.slots.add(slot);
        }
        const active = slot;
        active.job = job;
        active.worker.onmessage = event => {
          if (active.job !== job || event.data.id !== job.id) return;
          active.job = undefined;
          job.cleanup();
          if ('error' in event.data) {
            const error = new Error(event.data.error.message);
            error.name = event.data.error.name;
            job.reject(error);
          } else {
            job.resolve(event.data.result);
          }
          this.pump();
        };
        const fail = (error: Error): void => {
          if (active.job !== job) return;
          active.worker.terminate();
          this.slots.delete(active);
          active.job = undefined;
          this.reject(job, error);
          this.pump();
        };
        active.worker.onerror = event => {
          event.preventDefault();
          fail(new Error(event.message || 'Preparation worker failed'));
        };
        active.worker.onmessageerror = () => fail(new Error('Invalid preparation worker message'));
        // Copy cached source data only when a worker slot is available. Transfers
        // must never detach the provider's raw cache or a live mesh's UV source.
        let request = job.request;
        const transfer: Transferable[] = [];
        if (request.kind === 'vector') {
          request = { ...request, buffers: request.buffers.map(buffer => buffer.slice(0)) };
          transfer.push(...request.buffers);
        } else if (request.kind === 'terrain') {
          transfer.push(request.buffer);
        } else if (request.kind === 'uv') {
          request = { ...request, positions: new Float32Array(request.positions) };
          transfer.push(request.positions.buffer);
        }
        active.worker.postMessage({ id: job.id, request }, transfer);
      } catch (error) {
        if (slot) {
          slot.worker.terminate();
          this.slots.delete(slot);
        }
        this.reject(job, error);
      }
    }
  }
}
